import React from 'react';
import { motion } from 'framer-motion';
import { TopographicContours } from '../components/TopographicContours';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const terrain: string[] = ['Earliest stage is invisible', 'Deep tech runs on a different clock', 'Science does not always translate to investable narratives'];
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-4 mb-6" data-id="element-564" data-mp="1ctscfxtr4hq1">
      <span className="h-px w-10 bg-vitalityTeal/50" aria-hidden="true" data-id="element-565" data-mp="1hrk6oyhvcfxe" />
      <span className="text-xs uppercase tracking-[0.28em] text-vitalityTeal" data-id="element-566" data-mp="1hphhuhsgcsu9">
        {children}
      </span>
    </div>;
}
export function Foundation() {
  return <div className="w-full bg-[#10322a] text-myceliumWhite" data-id="element-567" data-mp="18pkmxyfgyxqr">
      {/* SECTION 1 — HERO */}
      <section className="relative overflow-hidden border-b border-white/5" data-id="element-568" data-mp="2c8o2u231539g">
        <div className="absolute inset-0 opacity-40" aria-hidden="true" data-id="element-569" data-mp="288ndhmee77nm">
          <TopographicContours data-id="element-570" data-mp="28agn9akay7zu" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b241d]/70 via-transparent to-[#10322a]" data-id="element-571" data-mp="2m6u36f96u1c7" />

        <div className="relative mx-auto w-full max-w-3xl px-6 md:px-10 py-20 md:py-24 flex flex-col items-center text-center" data-id="element-572" data-mp="2ha3mpqth13q9">
          <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="text-xs uppercase tracking-[0.32em] text-myceliumWhite/45" data-id="element-573" data-mp="1th5261wha1al">
            Vitality
          </motion.span>

          <div className="mt-10 md:mt-12" data-id="element-574" data-mp="1aqvp1wz5kp44">
            <div data-id="element-575" data-mp="15twxnm6viw9m">
              <motion.h1 initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: 0.06,
              ease: EASE
            }} className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] text-white" data-id="element-576" data-mp="15uewmqkx7ol8">
                The Vitality Foundation
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: 0.12,
              ease: EASE
            }} className="mt-5 text-xl md:text-2xl leading-snug text-vitalityTeal max-w-xl mx-auto" data-id="element-577" data-mp="2mzdbn5t9bjuj">
                We fuel the explorers building what the world needs next.
              </motion.p>
            </div>

            <motion.p initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.18,
            ease: EASE
          }} className="mt-8 text-base md:text-lg leading-relaxed text-myceliumWhite/60 max-w-xl mx-auto" data-id="element-578" data-mp="qcc45ekavo9f">
              They work at the edge of what is possible, on terrain the current
              capital system has never mapped. We are building the structure
              that carries them.
            </motion.p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — THE GAP WE HAVE SEEN */}
      <section className="mx-auto w-full max-w-6xl px-6 md:px-10 py-16 md:py-24" data-id="element-579" data-mp="1rzbirjxavpj4">
        <div className="grid md:grid-cols-12 gap-x-12 gap-y-8" data-id="element-580" data-mp="1nzb7u99etwgt">
          <motion.div {...reveal} className="md:col-span-4" data-id="element-581" data-mp="1v1prjebodyuo">
            <SectionLabel data-id="element-582" data-mp="1v299gv1bsn8p">The gap we have seen</SectionLabel>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.1] text-white" data-id="element-583" data-mp="200szb6j1ij6c">
              We kept meeting the same founder
            </h2>
          </motion.div>

          <div className="md:col-span-8" data-id="element-584" data-mp="1q1llyjcb2oj8">
            <motion.p {...reveal} transition={{
            duration: 0.3,
            delay: 0.06,
            ease: EASE
          }} className="text-lg md:text-xl leading-relaxed text-myceliumWhite/70" data-id="element-585" data-mp="1q0jz7hlxgdlo">
              Over the past two years we have talked with dozens of founders
              building real world solutions rooted in nature's own engineering.
              Soil systems. Materials science. Regenerative infrastructure.
              Again and again we saw the same pattern. Strong science. Real
              traction. And a capital system that could not make sense of their
              timeline.
            </motion.p>

            <motion.blockquote {...reveal} transition={{
            duration: 0.3,
            delay: 0.12,
            ease: EASE
          }} className="mt-10 border-l border-vitalityTeal/40 pl-6 md:pl-8" data-id="element-586" data-mp="er8id3etujca">
              <p className="font-serif italic text-2xl md:text-3xl leading-snug text-white/90" data-id="element-587" data-mp="jn6zc5xtsz2o">
                These founders are building on a different clock than the one
                most investors are calibrated to. We think that is a good thing.
              </p>
            </motion.blockquote>
          </div>
        </div>
      </section>

      {/* SECTION 3 — THE TERRAIN */}
      <section className="border-y border-white/5 bg-[#0d2b23]" data-id="element-588" data-mp="12fhz3qx6ogwk">
        <div className="mx-auto w-full max-w-6xl px-6 md:px-10 py-16 md:py-24" data-id="element-589" data-mp="1ge7wl7nx0ilg">
          <div className="grid md:grid-cols-12 gap-x-12 gap-y-10" data-id="element-590" data-mp="1bfo5ytydmimr">
            <motion.div {...reveal} className="md:col-span-4" data-id="element-591" data-mp="1bhhe6don1awv">
              <SectionLabel data-id="element-592" data-mp="93b92oidzmyd">The terrain</SectionLabel>
              <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.1] text-white" data-id="element-593" data-mp="90fcp4m6t6c1">
                Where the route gets hardest
              </h2>
            </motion.div>

            <ul className="md:col-span-8 border-t border-vitalityTeal/20" data-id="element-594" data-mp="i13nlqgaaqkp">
              {terrain.map((point, index) => <motion.li key={point} {...reveal} transition={{
              duration: 0.3,
              delay: index * 0.06,
              ease: EASE
            }} className="flex items-baseline gap-5 py-6 md:py-7 border-b border-vitalityTeal/20" data-id="element-595" data-mp="rt9e0vqw0zxs" data-mpi={index}>
                  <span className="mt-2 h-px w-6 shrink-0 bg-vitalityTeal" aria-hidden="true" data-id="element-596" data-mp="15q6g5tcgi3tz" />
                  <span className="text-xl md:text-2xl leading-snug text-white/90" data-id="element-597" data-mp="37kx9emq96keh">
                    {point}
                  </span>
                </motion.li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT WE ARE BUILDING + CLOSING */}
      <section className="bg-[#0b241d]" data-id="element-598" data-mp="2qbl8kxq9xft7">
        <div className="mx-auto w-full max-w-6xl px-6 md:px-10 py-16 md:py-24" data-id="element-599" data-mp="2q8h03o5rzxwq">
          <div className="grid md:grid-cols-12 gap-x-12 gap-y-8" data-id="element-600" data-mp="2maj5u6x5elmh">
            <motion.div {...reveal} className="md:col-span-4" data-id="element-601" data-mp="3izh5a2fuf7pz">
              <SectionLabel data-id="element-602" data-mp="3e04hnfh5dwmc">What we are building</SectionLabel>
              <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.1] text-white" data-id="element-603" data-mp="3e0noa01ckyl2">
                Infrastructure beats a single check
              </h2>
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.3,
            delay: 0.06,
            ease: EASE
          }} className="md:col-span-8 text-lg md:text-xl leading-relaxed text-myceliumWhite/70" data-id="element-604" data-mp="34z6born5ff14">
              The Vitality Foundation exists to give nature inspired founders
              the patient capital and the operating support this kind of work
              actually requires. It is not project based, not a grant that runs
              out, not a fund cycle that resets. A structure designed to keep
              showing up for this category over time. It is designed to be
              permanent.
            </motion.p>
          </div>

          <div className="mt-16 md:mt-24 pt-12 md:pt-16 border-t border-white/10 grid md:grid-cols-12 gap-x-12 gap-y-12" data-id="element-605" data-mp="l6h1at3vy1ht">
            <div className="md:col-span-7" data-id="element-606" data-mp="6gszzrvpf2u3">
              <motion.p {...reveal} className="font-serif italic text-3xl md:text-5xl leading-[1.15] text-white" data-id="element-607" data-mp="6fq9hl2an2ep">
                We are building for people who are not born yet.
              </motion.p>

              <motion.p {...reveal} transition={{
              duration: 0.3,
              delay: 0.06,
              ease: EASE
            }} className="mt-8 text-lg md:text-xl leading-relaxed text-myceliumWhite/60 max-w-[32rem]" data-id="element-608" data-mp="13y9v2pqs3qql">
                This is the long run. We look at decades rather than quarters or
                years.
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.3,
            delay: 0.12,
            ease: EASE
          }} className="md:col-span-5 md:border-l md:border-white/10 md:pl-12" data-id="element-609" data-mp="25orkglpdrrji">
              <p className="text-sm uppercase tracking-[0.28em] text-vitalityTeal" data-id="element-610" data-mp="2encxy2fzwgdf">
                Coming soon
              </p>
              <p className="mt-5 text-base leading-relaxed text-myceliumWhite/55" data-id="element-611" data-mp="3q4cp2uqlwp5v">
                For now this is by conversation only. Reach out directly if you
                would like to talk.
              </p>
              <a href="mailto:natalya@vitalityvf.com" className="mt-4 inline-block text-base text-white underline decoration-vitalityTeal/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-vitalityTeal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-vitalityTeal" data-id="element-612" data-mp="1qxmphv7yzydl">
                natalya@vitalityvf.com
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}